/**
 * Image to Flow — Paste a flowchart image, get Mermaid code + SVG preview.
 * Includes auto-fix: if generated code has syntax errors, sends it to Claude for correction.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Clipboard,
  Loader2,
  Save,
  Trash2,
  RefreshCw,
  Edit3,
  X,
  Image,
  Code,
  Eye,
  ChevronDown,
  ChevronUp,
  Wand2,
  FileText,
  Search,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from 'lucide-react';
import mermaid from 'mermaid';
import { docProcessingApi, FlowDiagram, DocumentInfo } from '../../services/docProcessingApi';

// Initialize mermaid with a clean theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'strict',
  fontFamily: 'sans-serif',
});

interface RenderResult {
  svg: string;
  error: string | null;
}

export default function ImageToFlow() {
  // ── Paste / Convert state ──────────────────────────────────
  const [pastedImage, setPastedImage] = useState<string | null>(null);
  const [pastedMimeType, setPastedMimeType] = useState<string>('image/png');
  const [mermaidCode, setMermaidCode] = useState<string>('');
  const [svgHtml, setSvgHtml] = useState<string>('');
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Document selector state (Append flow to doc) ─────────
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<DocumentInfo[]>([]);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const docDropdownRef = useRef<HTMLDivElement>(null);

  // ── Saved diagrams state ───────────────────────────────────
  const [savedDiagrams, setSavedDiagrams] = useState<FlowDiagram[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSvg, setEditSvg] = useState('');
  const [editSyntaxError, setEditSyntaxError] = useState<string | null>(null);
  const [editFixing, setEditFixing] = useState(false);
  const [editZoom, setEditZoom] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedSvg, setExpandedSvg] = useState<string>('');
  const [expandedError, setExpandedError] = useState<string | null>(null);
  // Resizable columns: store widths as percentages (sum to 100)
  const [col1Width, setCol1Width] = useState(33.33);
  const [col2Width, setCol2Width] = useState(33.33);
  const [col3Width, setCol3Width] = useState(33.34);
  const [draggingDivider, setDraggingDivider] = useState<'1-2' | '2-3' | null>(null);

  const pasteAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track whether we should auto-fix after initial conversion
  const autoFixRef = useRef(false);

  // ── Load saved diagrams ────────────────────────────────────
  const loadDiagrams = useCallback(async () => {
    setLoadingList(true);
    try {
      const resp = await docProcessingApi.listFlowDiagrams();
      setSavedDiagrams(resp.entries);
    } catch (err) {
      console.error('Failed to load flow diagrams:', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadDiagrams();
  }, [loadDiagrams]);

  // ── Load documents for the dropdown ──────────────────────
  useEffect(() => {
    (async () => {
      setLoadingDocs(true);
      try {
        const resp = await docProcessingApi.listDocuments();
        // Only show ingested documents (they have chunks to append to)
        // Normalise filenames to lowercase so sorting is case-insensitive
        // Deduplicate by filename so each file appears once (same name in different paths → keep first)
        const seen = new Set<string>();
        const ingested = resp.documents
          .filter((d) => d.is_ingested)
          .map((d) => ({
            ...d,
            filename: d.filename.toLowerCase(),
          }))
          .sort((a, b) => a.filename.localeCompare(b.filename))
          .filter((d) => {
            if (seen.has(d.filename)) return false;
            seen.add(d.filename);
            return true;
          });
        setDocuments(ingested);
      } catch (err) {
        console.error('Failed to load documents:', err);
      } finally {
        setLoadingDocs(false);
      }
    })();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (docDropdownRef.current && !docDropdownRef.current.contains(e.target as Node)) {
        setDocDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter documents by search query (contains, not starts-with)
  const filteredDocs = useMemo(() => {
    if (!docSearchQuery.trim()) return documents;
    const q = docSearchQuery.toLowerCase().trim();
    return documents.filter(
      (d) =>
        d.filename.toLowerCase().includes(q) ||
        (d.category && d.category.toLowerCase().includes(q)) ||
        (d.gcs_path && d.gcs_path.toLowerCase().includes(q))
    );
  }, [documents, docSearchQuery]);

  const docsToShow = useMemo(() => {
    return showAllDocs ? documents : filteredDocs;
  }, [showAllDocs, documents, filteredDocs]);

  // ── Render Mermaid code to SVG ─────────────────────────────
  // Clean up orphan DOM elements that mermaid leaves behind on render failure.
  // Mermaid creates error SVGs in document.body that persist across navigation.
  const cleanupMermaidOrphans = useCallback((id: string) => {
    // Remove the element mermaid created for this render attempt
    document.getElementById(id)?.remove();
    // Mermaid also wraps in a container div with id `d${id}`
    document.getElementById(`d${id}`)?.remove();
    // Remove any lingering mermaid error SVGs (class="error-icon" or aria-roledescription="error")
    document.querySelectorAll('[aria-roledescription="error"]').forEach((el) => el.remove());
  }, []);

  const renderMermaid = useCallback(async (code: string): Promise<RenderResult> => {
    if (!code.trim()) return { svg: '', error: null };
    const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    try {
      const { svg } = await mermaid.render(id, code);
      return { svg, error: null };
    } catch (err: any) {
      cleanupMermaidOrphans(id);
      const message = err?.message || err?.str || 'Unknown mermaid syntax error';
      return { svg: '', error: message };
    }
  }, [cleanupMermaidOrphans]);

  // Clean up any mermaid orphans when this component unmounts
  useEffect(() => {
    return () => {
      document.querySelectorAll('[aria-roledescription="error"]').forEach((el) => el.remove());
    };
  }, []);

  // Re-render SVG when mermaid code changes (main editor)
  useEffect(() => {
    if (!mermaidCode) {
      setSvgHtml('');
      setSyntaxError(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await renderMermaid(mermaidCode);
      if (cancelled) return;
      setSvgHtml(result.svg);
      setSyntaxError(result.error);

      // Auto-fix: if this came from initial conversion and there's an error, auto-fix once
      if (result.error && autoFixRef.current) {
        autoFixRef.current = false;
        setFixing(true);
        try {
          const resp = await docProcessingApi.fixMermaidCode(mermaidCode, result.error);
          if (!cancelled) {
            setMermaidCode(resp.mermaid_code);
          }
        } catch (fixErr: any) {
          if (!cancelled) {
            setError(`Auto-fix failed: ${fixErr.message || 'Unknown error'}`);
          }
        } finally {
          if (!cancelled) setFixing(false);
        }
      }
    }, 400); // Debounce 400ms

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mermaidCode, renderMermaid]);

  // ── Fix with AI (manual) ───────────────────────────────────
  const handleFix = async () => {
    if (!mermaidCode.trim()) return;
    setFixing(true);
    setError(null);
    try {
      const resp = await docProcessingApi.fixMermaidCode(mermaidCode, syntaxError || undefined);
      setMermaidCode(resp.mermaid_code);
    } catch (err: any) {
      setError(err.message || 'Fix failed');
    } finally {
      setFixing(false);
    }
  };

  // ── Fix in edit modal ──────────────────────────────────────
  const handleEditFix = async () => {
    if (!editCode.trim()) return;
    setEditFixing(true);
    try {
      const resp = await docProcessingApi.fixMermaidCode(editCode, editSyntaxError || undefined);
      setEditCode(resp.mermaid_code);
    } catch (err: any) {
      setError(err.message || 'Fix failed');
    } finally {
      setEditFixing(false);
    }
  };

  // ── Handle paste event ─────────────────────────────────────
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const mime = item.type;
        setPastedMimeType(mime);

        const reader = new FileReader();
        reader.onload = async () => {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1];
          setPastedImage(dataUrl);
          setError(null);
          setMermaidCode('');
          setSvgHtml('');
          setSyntaxError(null);

          // Auto-convert
          autoFixRef.current = true; // Enable auto-fix for this conversion
          setConverting(true);
          try {
            const resp = await docProcessingApi.convertImageToFlow(base64, mime);
            setMermaidCode(resp.mermaid_code);
          } catch (err: any) {
            setError(err.message || 'Conversion failed');
            autoFixRef.current = false;
          } finally {
            setConverting(false);
          }
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  }, []);

  // ── Handle file browse ─────────────────────────────────────
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const mime = file.type;
    setPastedMimeType(mime);

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      setPastedImage(dataUrl);
      setError(null);
      setMermaidCode('');
      setSvgHtml('');
      setSyntaxError(null);

      autoFixRef.current = true; // Enable auto-fix for this conversion
      setConverting(true);
      try {
        const resp = await docProcessingApi.convertImageToFlow(base64, mime);
        setMermaidCode(resp.mermaid_code);
      } catch (err: any) {
        setError(err.message || 'Conversion failed');
        autoFixRef.current = false;
      } finally {
        setConverting(false);
      }
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  }, []);

  // Attach paste listener
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // ── Re-generate from same image ────────────────────────────
  const handleRegenerate = async () => {
    if (!pastedImage) return;
    const base64 = pastedImage.split(',')[1];
    autoFixRef.current = true; // Enable auto-fix for re-generation too
    setConverting(true);
    setError(null);
    setSyntaxError(null);
    try {
      const resp = await docProcessingApi.convertImageToFlow(base64, pastedMimeType);
      setMermaidCode(resp.mermaid_code);
    } catch (err: any) {
      setError(err.message || 'Conversion failed');
      autoFixRef.current = false;
    } finally {
      setConverting(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!mermaidCode.trim() || !title.trim()) return;
    setSaving(true);
    try {
      const paths = selectedDocs.map((d) => d.gcs_path).filter(Boolean) as string[];
      await docProcessingApi.saveFlowDiagram({
        title,
        mermaid_code: mermaidCode,
        description: description || null,
        source_path: paths.length === 1 ? paths[0] : null,
        source_paths: paths.length > 1 ? paths : undefined,
      });
      setPastedImage(null);
      setMermaidCode('');
      setSvgHtml('');
      setSyntaxError(null);
      setTitle('');
      setDescription('');
      setSelectedDocs([]);
      setDocSearchQuery('');
      setError(null);
      loadDiagrams();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Clear ──────────────────────────────────────────────────
  const handleClear = () => {
    setPastedImage(null);
    setMermaidCode('');
    setSvgHtml('');
    setSyntaxError(null);
    setTitle('');
    setDescription('');
      setSelectedDocs([]);
      setDocSearchQuery('');
      setShowAllDocs(false);
      setError(null);
      autoFixRef.current = false;
  };

  // ── Delete saved diagram ───────────────────────────────────
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this flow diagram?')) return;
    try {
      await docProcessingApi.deleteFlowDiagram(id);
      setSavedDiagrams((prev) => prev.filter((d) => d.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  // ── Edit saved diagram ─────────────────────────────────────
  const startEdit = (diagram: FlowDiagram) => {
    setEditingId(diagram.id);
    setEditCode(diagram.mermaid_code);
    setEditTitle(diagram.title);
    setEditDescription(diagram.description || '');
    setEditZoom(1);
    setEditSyntaxError(null);
    renderMermaid(diagram.mermaid_code).then((r) => {
      setEditSvg(r.svg);
      setEditSyntaxError(r.error);
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditCode('');
    setEditTitle('');
    setEditDescription('');
    setEditSvg('');
    setEditSyntaxError(null);
    setEditZoom(1);
  };

  const saveEdit = async () => {
    if (editingId === null) return;
    try {
      const resp = await docProcessingApi.updateFlowDiagram(editingId, {
        title: editTitle,
        mermaid_code: editCode,
        description: editDescription || null,
      });
      setSavedDiagrams((prev) =>
        prev.map((d) => (d.id === editingId ? resp.entry : d))
      );
      cancelEdit();
    } catch (err: any) {
      setError(err.message || 'Update failed');
    }
  };

  // Re-render edit SVG when edit code changes (debounced)
  useEffect(() => {
    if (editingId === null || !editCode) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await renderMermaid(editCode);
      if (!cancelled) {
        setEditSvg(result.svg);
        setEditSyntaxError(result.error);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editCode, editingId, renderMermaid]);

  // Handle column resizing via drag dividers
  useEffect(() => {
    if (!draggingDivider) return;

    const handleMouseMove = (e: MouseEvent) => {
      const modalBody = document.querySelector('[data-edit-modal-body]') as HTMLElement;
      if (!modalBody) return;

      const rect = modalBody.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const totalWidth = rect.width;
      const newPercent = Math.max(10, Math.min(80, (relativeX / totalWidth) * 100));

      if (draggingDivider === '1-2') {
        const newCol2 = Math.max(10, col2Width + (col1Width - newPercent));
        setCol1Width(newPercent);
        setCol2Width(newCol2);
        setCol3Width(100 - newPercent - newCol2);
      } else if (draggingDivider === '2-3') {
        const newCol3 = 100 - newPercent;
        setCol2Width(newPercent - col1Width);
        setCol3Width(Math.max(10, newCol3));
      }
    };

    const handleMouseUp = () => {
      setDraggingDivider(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingDivider, col1Width, col2Width]);

  // ── Expand/preview a saved diagram ─────────────────────────
  const toggleExpand = async (diagram: FlowDiagram) => {
    if (expandedId === diagram.id) {
      setExpandedId(null);
      setExpandedSvg('');
      setExpandedError(null);
    } else {
      setExpandedId(diagram.id);
      const result = await renderMermaid(diagram.mermaid_code);
      setExpandedSvg(result.svg);
      setExpandedError(result.error);
    }
  };

  return (
    <div className="space-y-3">
      {/* ── Paste Area + Results ───────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
          <Image size={16} />
          Paste or Browse Image
        </h3>

        {/* Paste target / image preview */}
        {!pastedImage ? (
          <div
            ref={pasteAreaRef}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center
                       hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer"
          >
            <Clipboard size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-gray-600 font-medium text-sm">
              Paste an image here (Ctrl+V)
            </p>
            <p className="text-gray-400 text-xs mt-1">
              or click to browse for an image file
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Split view: Image | Mermaid output */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Left: Pasted image */}
              <div>
                <div className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                  <Image size={14} /> Source Image
                </div>
                <div className="border rounded-lg p-2 bg-gray-50 max-h-80 overflow-auto">
                  <img
                    src={pastedImage}
                    alt="Pasted flowchart"
                    className="max-w-full h-auto"
                  />
                </div>
              </div>

              {/* Right: Mermaid code + SVG preview */}
              <div className="space-y-3">
                {converting || fixing ? (
                  <div className="flex items-center justify-center h-40 text-blue-600">
                    <Loader2 size={24} className="animate-spin mr-2" />
                    {fixing ? 'Fixing syntax with AI...' : 'Converting with Claude vision...'}
                  </div>
                ) : (
                  <>
                    {/* Editable Mermaid code */}
                    <div>
                      <div className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <Code size={14} /> Mermaid Code
                      </div>
                      <textarea
                        value={mermaidCode}
                        onChange={(e) => {
                          autoFixRef.current = false; // Disable auto-fix for manual edits
                          setMermaidCode(e.target.value);
                        }}
                        className="w-full h-36 font-mono text-sm border rounded-lg p-3 bg-gray-50
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                        placeholder="Mermaid code will appear here..."
                      />
                    </div>

                    {/* Syntax error banner with Fix button */}
                    {syntaxError && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-800">Mermaid Syntax Error</p>
                            <p className="text-xs text-amber-600 mt-1 font-mono break-all">
                              {syntaxError.slice(0, 200)}
                            </p>
                          </div>
                          <button
                            onClick={handleFix}
                            disabled={fixing}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white
                                       text-sm font-medium rounded-lg hover:bg-amber-700
                                       disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {fixing ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Wand2 size={14} />
                            )}
                            Fix with AI
                          </button>
                        </div>
                      </div>
                    )}

                    {/* SVG Preview */}
                    {svgHtml && (
                      <div>
                        <div className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                          <Eye size={14} /> Preview
                        </div>
                        <div
                          className="border rounded-lg p-3 bg-white max-h-64 overflow-auto"
                          dangerouslySetInnerHTML={{ __html: svgHtml }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">
                {error}
              </div>
            )}

            {/* Save form + actions */}
            {mermaidCode && !converting && !fixing && (
              <div className="border-t pt-3 space-y-2">
                {/* Description + Title side by side (description wider & taller) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      Description (optional)
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Brief description of the flow (supports longer text)..."
                      rows={6}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none
                                 focus:ring-2 focus:ring-blue-500 resize-y min-h-[140px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Login Flow, Order Process..."
                      rows={3}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none
                                 focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                </div>

                {/* Append flow to document — searchable dropdown (contains), multi-select */}
                <div ref={docDropdownRef} className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    <FileText size={12} className="inline mr-1" />
                    Append flow to document (makes it searchable)
                  </label>
                  <div
                    className={`flex items-center border rounded-lg px-3 py-1.5 text-sm cursor-pointer min-h-[2.25rem]
                                ${docDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300'}
                                ${selectedDocs.length > 0 ? 'bg-blue-50' : 'bg-white'}`}
                    onClick={() => setDocDropdownOpen(!docDropdownOpen)}
                  >
                    <Search size={14} className="text-gray-400 mr-2 flex-shrink-0" />
                    {selectedDocs.length > 0 ? (
                      <div className="flex-1 flex flex-wrap items-center gap-1 min-w-0 py-0.5">
                        {selectedDocs.map((doc) => (
                          <span
                            key={doc.gcs_path || doc.filename}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-xs max-w-[180px] truncate"
                          >
                            {doc.filename}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDocs((prev) => prev.filter((d) => d.gcs_path !== doc.gcs_path));
                              }}
                              className="p-0.5 hover:bg-blue-200 rounded"
                            >
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={docSearchQuery}
                        onChange={(e) => {
                          setDocSearchQuery(e.target.value);
                          setShowAllDocs(false);
                          setDocDropdownOpen(true);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocDropdownOpen(true);
                        }}
                        placeholder={loadingDocs ? 'Loading documents...' : `Search by name (contains) — ${documents.length} documents`}
                        className="flex-1 bg-transparent outline-none text-sm min-w-0"
                      />
                    )}
                    <ChevronDown size={14} className="text-gray-400 flex-shrink-0 ml-1" />
                  </div>

                  {/* Dropdown list — Show all link + list with contains filter */}
                  {docDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {loadingDocs ? (
                        <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Loading...
                        </div>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-1.5 flex items-center justify-between">
                            <span className="text-xs text-gray-500">Search matches <strong>contains</strong> (not starts with)</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAllDocs(true);
                                setDocSearchQuery('');
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Show all documents
                            </button>
                          </div>
                          {docsToShow.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-400">
                              {docSearchQuery.trim() ? 'No documents match (contains). Try a shorter term or show all.' : 'No documents found'}
                            </div>
                          ) : (
                            docsToShow.slice(0, showAllDocs ? undefined : 100).map((doc) => {
                              const isSelected = selectedDocs.some((d) => d.gcs_path === doc.gcs_path);
                              return (
                                <div
                                  key={doc.gcs_path || doc.filename}
                                  className="px-3 py-1.5 text-sm hover:bg-blue-50 cursor-pointer flex items-center gap-2"
                                  onClick={() => {
                                    setSelectedDocs((prev) =>
                                      isSelected ? prev.filter((d) => d.gcs_path !== doc.gcs_path) : [...prev, doc]
                                    );
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="rounded border-gray-300 text-blue-600"
                                  />
                                  <span className="truncate flex-1">{doc.filename}</span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">
                                    {doc.category} &middot; {doc.chunk_count} chunks
                                  </span>
                                </div>
                              );
                            })
                          )}
                          {!showAllDocs && docsToShow.length > 100 && (
                            <div className="px-3 py-1.5 text-xs text-gray-400 border-t">
                              Showing first 100. Type to narrow or use &quot;Show all documents&quot;.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving || !title.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm
                               font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50
                               disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    {selectedDocs.length > 0 ? `Save & Index${selectedDocs.length > 1 ? ` (${selectedDocs.length} docs)` : ''}` : 'Save'}
                  </button>
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm
                               font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <RefreshCw size={14} />
                    Re-generate
                  </button>
                  {syntaxError && (
                    <button
                      onClick={handleFix}
                      disabled={fixing}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 text-sm
                                 font-medium rounded-lg hover:bg-amber-200 transition-colors"
                    >
                      <Wand2 size={14} />
                      Fix with AI
                    </button>
                  )}
                  <button
                    onClick={handleClear}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm
                               font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <X size={14} />
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Saved Diagrams Table ───────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Saved Flow Diagrams</h3>
          <span className="text-xs text-gray-400">{savedDiagrams.length} saved</span>
        </div>

        {loadingList ? (
          <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" />
            Loading...
          </div>
        ) : savedDiagrams.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            No flow diagrams saved yet. Paste an image above to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {savedDiagrams.map((diagram) => (
              <div key={diagram.id}>
                {/* Row */}
                <div className="px-4 py-2 flex items-center gap-3 hover:bg-gray-50">
                  <button
                    onClick={() => toggleExpand(diagram)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {expandedId === diagram.id ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm truncate">
                      {diagram.title}
                    </div>
                    {diagram.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {diagram.description}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {diagram.created_at
                      ? new Date(diagram.created_at).toLocaleDateString()
                      : '—'}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEdit(diagram)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded
                                 transition-colors"
                      title="Edit"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(diagram.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded
                                 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Expanded preview */}
                {expandedId === diagram.id && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Mermaid Code</div>
                        <pre className="text-xs font-mono bg-white border rounded p-3 max-h-48 overflow-auto whitespace-pre-wrap">
                          {diagram.mermaid_code}
                        </pre>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Preview</div>
                        {expandedSvg ? (
                          <div
                            className="bg-white border rounded p-3 max-h-48 overflow-auto"
                            dangerouslySetInnerHTML={{ __html: expandedSvg }}
                          />
                        ) : expandedError ? (
                          <div className="bg-amber-50 border border-amber-200 rounded p-3 max-h-48 overflow-auto">
                            <p className="text-xs font-medium text-amber-800">Syntax Error</p>
                            <p className="text-xs text-amber-600 font-mono break-all mt-1">
                              {expandedError.slice(0, 300)}
                            </p>
                          </div>
                        ) : (
                          <div className="bg-gray-50 border rounded p-3 max-h-48 flex items-center justify-center text-gray-400 text-xs">
                            Loading...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Edit Modal (full-screen) ──────────────────────────── */}
      {editingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-xl w-full h-full max-w-[98vw] max-h-[96vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-2.5 border-b shrink-0">
              <h3 className="text-sm font-semibold text-gray-900">Edit Flow Diagram</h3>
              <button
                onClick={cancelEdit}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal body — three-column layout: Title+Description | Preview | Code */}
            <div className="flex-1 overflow-hidden p-4 min-h-0" data-edit-modal-body>
              <div className="flex h-full gap-0" style={{ userSelect: draggingDivider ? 'none' : 'auto' }}>

                {/* Column 1: Title + Description */}
                <div className="flex flex-col" style={{ width: `${col1Width}%`, minWidth: '0' }}>
                  {/* Title - fixed height */}
                  <div className="shrink-0 pb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Title</label>
                    <textarea
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      rows={2}
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none
                                 focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Description - fills remaining space */}
                  <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5 shrink-0">Description</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Brief description of the flow..."
                      className="w-full flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none
                                 focus:ring-2 focus:ring-blue-500 resize-none overflow-y-auto"
                    />
                  </div>
                </div>

                {/* Divider 1-2 */}
                <div
                  onMouseDown={() => setDraggingDivider('1-2')}
                  className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors"
                  title="Drag to resize columns"
                />

                {/* Column 2: Preview */}
                <div className="flex flex-col overflow-y-auto" style={{ width: `${col2Width}%`, minWidth: '0' }}>
                  <div className="flex items-center justify-between mb-0.5 shrink-0">
                    <label className="text-xs font-medium text-gray-700">Preview</label>
                    {editSvg && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditZoom((z) => Math.max(0.25, z - 0.25))}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded
                                     transition-colors"
                          title="Zoom out"
                        >
                          <ZoomOut size={14} />
                        </button>
                        <button
                          onClick={() => setEditZoom(1)}
                          className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700
                                     hover:bg-gray-100 rounded transition-colors font-mono min-w-[3rem] text-center"
                          title="Reset zoom"
                        >
                          {Math.round(editZoom * 100)}%
                        </button>
                        <button
                          onClick={() => setEditZoom((z) => Math.min(3, z + 0.25))}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded
                                     transition-colors"
                          title="Zoom in"
                        >
                          <ZoomIn size={14} />
                        </button>
                        <button
                          onClick={() => setEditZoom(1)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded
                                     transition-colors ml-0.5"
                          title="Reset zoom to 100%"
                        >
                          <RotateCcw size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                  {editSvg ? (
                    <div className="border rounded-lg bg-white flex-1 overflow-auto">
                      <div
                        style={{ transform: `scale(${editZoom})`, transformOrigin: 'top left' }}
                        className="p-3"
                        dangerouslySetInnerHTML={{ __html: editSvg }}
                      />
                    </div>
                  ) : editSyntaxError ? (
                    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex-1 overflow-auto">
                      <p className="text-sm font-medium text-amber-800 mb-1">Syntax Error</p>
                      <p className="text-xs text-amber-600 font-mono break-all">
                        {editSyntaxError.slice(0, 300)}
                      </p>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-3 bg-gray-50 flex-1 flex items-center justify-center text-gray-400 text-sm">
                      Preview will appear here
                    </div>
                  )}
                </div>

                {/* Divider 2-3 */}
                <div
                  onMouseDown={() => setDraggingDivider('2-3')}
                  className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors"
                  title="Drag to resize columns"
                />

                {/* Column 3: Mermaid Code */}
                <div className="flex flex-col overflow-y-auto" style={{ width: `${col3Width}%`, minWidth: '0' }}>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5 shrink-0">
                    Mermaid Code
                  </label>
                  <textarea
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value)}
                    className="w-full flex-1 font-mono text-sm border rounded-lg p-3 bg-gray-50
                               focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                  {/* Fix button inline under code */}
                  {editSyntaxError && (
                    <div className="flex items-center gap-2 mt-2 shrink-0">
                      <button
                        onClick={handleEditFix}
                        disabled={editFixing}
                        className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 text-xs
                                   font-medium rounded-lg hover:bg-amber-200 disabled:opacity-50
                                   transition-colors"
                      >
                        {editFixing ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Wand2 size={12} />
                        )}
                        Fix with AI
                      </button>
                      <span className="text-xs text-gray-500">Auto-fix syntax errors</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-2.5 border-t shrink-0">
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg
                           hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={!editTitle.trim() || !editCode.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white
                           bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50
                           transition-colors"
              >
                <Save size={14} />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
